import React, { useState, useEffect, useRef } from 'react'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { contarClientesPromocao, enviarPromocao, getConfiguracoes, updateConfiguracao } from '../../lib/supabase'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)', boxSizing: 'border-box',
}

export default function Promocoes() {
  const { tenant } = useTenant()
  const { utilizador } = useAuth()
  const toast = useToast()
  const [assunto, setAssunto] = useState('')
  const [html, setHtml] = useState('')
  const [total, setTotal] = useState(null)
  const [preview, setPreview] = useState(false)
  const [emailTeste, setEmailTeste] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [enviandoTeste, setEnviandoTeste] = useState(false)
  const [confirmar, setConfirmar] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const emailInicializado = useRef(false)

  useEffect(() => {
    if (!tenant) return
    contarClientesPromocao(tenant.id).then(setTotal).catch(() => setTotal(null))
    // Recarrega o último rascunho guardado (assunto + html)
    getConfiguracoes(tenant.id).then(cfg => {
      if (cfg?.promocao_assunto) setAssunto(cfg.promocao_assunto)
      if (cfg?.promocao_mensagem) setHtml(cfg.promocao_mensagem)
    }).catch(() => {})
  }, [tenant])

  async function guardarRascunho() {
    setGuardando(true)
    try {
      await Promise.all([
        updateConfiguracao(tenant.id, 'promocao_assunto', assunto),
        updateConfiguracao(tenant.id, 'promocao_mensagem', html),
      ])
      toast.sucesso('Rascunho guardado.')
    } catch (e) { toast.erro(e.message) } finally { setGuardando(false) }
  }

  // Pré-preenche o email de teste só uma vez (na abertura). Se o utilizador
  // limpar o campo, fica em branco — não voltamos a preencher.
  useEffect(() => {
    if (!emailInicializado.current && utilizador?.email) {
      setEmailTeste(utilizador.email)
      emailInicializado.current = true
    }
  }, [utilizador])

  function validar() {
    if (!assunto.trim()) { toast.erro('O assunto é obrigatório.'); return false }
    if (!html.trim()) { toast.erro('O conteúdo do email é obrigatório.'); return false }
    return true
  }

  async function enviarTeste() {
    if (!validar()) return
    if (!emailTeste.trim()) { toast.erro('Indica um email para o teste.'); return }
    if (!EMAIL_RE.test(emailTeste.trim())) { toast.erro('Indica um email válido para o teste.'); return }
    setEnviandoTeste(true)
    try {
      await enviarPromocao(tenant.id, { assunto, html, teste: true, email_teste: emailTeste.trim() })
      toast.sucesso(`Email de teste enviado para ${emailTeste.trim()}.`)
    } catch (e) { toast.erro(e.message) } finally { setEnviandoTeste(false) }
  }

  async function enviarTodos() {
    setConfirmar(false)
    if (!validar()) return
    setEnviando(true)
    try {
      const r = await enviarPromocao(tenant.id, { assunto, html })
      if (r.total === 0) toast.erro('Não há clientes com promoções ativas e email.')
      else if (r.ok) toast.sucesso(`Campanha enviada a ${r.enviados} cliente(s).`)
      else toast.erro(`Enviado a ${r.enviados} de ${r.total}. Alguns lotes falharam.`)
    } catch (e) { toast.erro(e.message) } finally { setEnviando(false) }
  }

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <h2 style={{ marginBottom: '6px', fontSize: '22px' }}>Promoções</h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '24px', fontSize: '14px' }}>
        Envia uma campanha por email a todos os clientes que aceitaram receber promoções.
        {total !== null && (
          <> Atualmente: <strong style={{ color: 'var(--cor-principal)' }}>{total} cliente(s)</strong> com promoções ativas.</>
        )}
      </p>

      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '24px', boxShadow: 'var(--sombra)' }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Assunto</label>
          <input type="text" value={assunto} onChange={e => setAssunto(e.target.value)} style={inputStyle} placeholder="Ex.: Promoção de Verão — 20% em todos os tratamentos" />
        </div>

        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500' }}>Conteúdo do email (HTML)</label>
            <button type="button" onClick={() => setPreview(true)} disabled={!html.trim()}
              style={{ fontSize: '12px', padding: '3px 10px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-borda)', background: 'var(--cor-fundo)', color: 'var(--cor-texto-suave)', cursor: html.trim() ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-ui)' }}>
              Pré-visualizar
            </button>
          </div>
          <textarea value={html} onChange={e => setHtml(e.target.value)} rows={12} spellCheck={false}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
            placeholder="Cola aqui o HTML do email (pode ser um template completo)…" />
        </div>
        <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginBottom: '20px' }}>
          O email é enviado a todos em <strong>BCC</strong> (ninguém vê os outros emails) e leva automaticamente um link de cancelamento de subscrição.
        </p>

        <div style={{ borderTop: '1px solid var(--cor-borda)', paddingTop: '18px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Enviar teste para</label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <input type="email" value={emailTeste} onChange={e => setEmailTeste(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: '200px' }} placeholder="o.teu@email.com" />
            <button type="button" onClick={enviarTeste} disabled={enviando || enviandoTeste} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px', opacity: (enviando || enviandoTeste) ? 0.6 : 1 }}>
              {enviandoTeste ? 'A enviar…' : 'Enviar teste'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={() => { if (validar()) setConfirmar(true) }} disabled={enviando || enviandoTeste} className="btn-primario" style={{ opacity: (enviando || enviandoTeste) ? 0.6 : 1 }}>
              {enviando ? 'A enviar…' : `Enviar a todos${total !== null ? ` (${total})` : ''}`}
            </button>
            <button type="button" onClick={guardarRascunho} disabled={guardando} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px', opacity: guardando ? 0.6 : 1 }}>
              {guardando ? 'A guardar…' : 'Guardar rascunho'}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div onClick={() => setPreview(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--raio-lg)', width: '100%', maxWidth: '620px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--cor-borda)' }}>
              <strong style={{ fontSize: '14px' }}>Pré-visualização</strong>
              <button onClick={() => setPreview(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--cor-texto-suave)', lineHeight: 1 }}>×</button>
            </div>
            <iframe srcDoc={html} title="Pré-visualização" style={{ flex: 1, border: 'none', minHeight: '460px' }} sandbox="allow-same-origin" />
          </div>
        </div>
      )}

      {confirmar && (
        <div onClick={() => setConfirmar(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--raio-lg)', width: '100%', maxWidth: '420px', padding: '24px' }}>
            <p style={{ fontWeight: '700', marginBottom: '8px', fontSize: '16px' }}>Confirmar envio</p>
            <p style={{ fontSize: '14px', color: 'var(--cor-texto-suave)', marginBottom: '20px', lineHeight: 1.5 }}>
              Vais enviar esta campanha a <strong style={{ color: 'var(--cor-principal)' }}>{total ?? '—'} cliente(s)</strong>. Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmar(false)} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px' }}>Cancelar</button>
              <button onClick={enviarTodos} className="btn-primario" style={{ padding: '9px 18px', fontSize: '14px' }}>Enviar agora</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
